// import '@ant-design/compatible/assets/index.css';


import { useQuery } from 'react-query';
import React, { useCallback, useEffect, useMemo } from 'react';

import { Spin, InputNumber, DatePicker, Row, Switch } from 'antd';
import DF from 'DisplayFramework';

import Form, { FormInstance } from 'antd/lib/form';

import _, { isEmpty, isNil } from 'lodash';
import { indexBy, setValueByNamePath, shouldUpdateCompare } from 'utils/utils2';

import { getAssetUseByAssetID } from 'services/assets';
import { DataGridColDef } from 'components/DataTable/DataTable';
import moment from 'moment';
import DataGrid from 'components/DataGrid';
import FormInput from 'components/FormInput';
import InputCurrency from 'components/InputCurrency';
import ParListSelect from 'components/ParListSelect';
import TextArea from 'antd/lib/input/TextArea';

export type TabAddAssetUseProps = {
  objectid: number;
  form: FormInstance;
  setSuccessful:any
};

const TabAddAssetUse = ({ objectid, form,setSuccessful }: TabAddAssetUseProps) => {
  const fieldName = 'listData';

  // const { data, refetch } = useGetAssetDetails(objectid, 'PROD');

  const { data: listAssetUse, isFetching } = useQuery(
    ['getAssetUseByAssetID', objectid!],
    () =>
      getAssetUseByAssetID({ assetid: objectid! }).then(
        (res) => res.list.map((use: any) => ({ ...use, useid: use.id })) || []
      ),
    {
      enabled: !isNil(objectid),
      staleTime: 60 * 1000 * 5,
    }
  );

  
  useEffect(() => {
    setSuccessful(isFetching);
  }, [isFetching]);


  const getName = useCallback(
    (name: string) => (useid: number) => {
      const listuse = form.getFieldValue(fieldName);
      const index = indexBy(listuse!, 'useid')[useid];
      return [fieldName, index, name];
    },
    [form]
  );

  useEffect(() => {
    if (!isNil(listAssetUse)) {
      form.setFieldsValue({
        [fieldName]: _.uniqBy(
          listAssetUse.filter((use: any) => use.usevaluetype === 'INIT'),
          'attcode'
        ).map((use) => ({
          ...use,
          lastregistredvalue: !isNil(use?.lastregistredusevalue)
            ? use?.lastregistredusevalue
            : ('' as any),
          lastregistredusedate: !isNil(use?.lastregistredusedate)
            ? use?.lastregistredusedate
            : ('' as any),
        })),
        event: 'tabAddAssetUse',
      });
    }
  }, [form, listAssetUse]);

  const columns = useMemo<DataGridColDef<any>[]>(
    () => [
      {
        comcode: 'attribute',
        dataIndex: 'uselabel',
        width: 140,
      },
      {
        comcode: 'agreedValue',
        dataIndex: 'usevalue',
        width: 140,
      },
      {
        dataIndex: 'lastregistredvalue',
        width: 140,
      },
      {
        dataIndex: 'lastregistredusedate',
        width: 140,
        render: (value, record, index) => {
          return !isNil(value) && !isEmpty(value)
            ? moment(value).format('YYYY-MM-DD')
            : '';
        },
      },
      {
        dataIndex: 'newusevalue',
        width: 140,
        render: (value, record) => {
          return (
            <Form.Item name={getName('newusevalue')(record.useid)} noStyle>
              <InputNumber
                style={{ width: '100%' }}
                min={
                  isNil(record.lastregistredvalue)
                    ? 0
                    : record.lastregistredvalue
                }
              />
            </Form.Item>
          );
        },
      },
      {
        dataIndex: 'newusevaluedate',
        width: 140,
        render: (value, record) => {
          return (
            <Form.Item shouldUpdate noStyle>
              {({ getFieldValue }) => (
                <DatePicker
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD"
                  value={getFieldValue(
                    getName('newusevaluedate')(record.useid)
                  )}
                  onChange={(val) => {
                    if (!isNil(val)) {
                      if (
                        !val.isSameOrBefore(
                          moment(record.lastregistredusedate).format(
                            'YYYY-MM-DD'
                          )
                        )
                      )
                        setValueByNamePath(
                          form,
                          getName('newusevaluedate')(record.useid),
                          val
                        );
                      else
                        setValueByNamePath(
                          form,
                          getName('newusevaluedate')(record.useid),
                          '' as any
                        );
                    }
                  }}
                />
              )}
            </Form.Item>
          );
        },
      },
    ],
    [getName]
  );

  return (
    <DF.Provider pagcode="tabAddAssetUse">
      <Form.Item
        name="usevalues"
        rules={[
          {
            validator() {
              if (
                !isNil(
                  form
                    .getFieldValue(fieldName)
                    .find(
                      (use: any) =>
                        isNil(use?.newusevalue) || isNil(use?.newusevaluedate)
                    )
                )
              ) {
                return Promise.reject(
                  new Error('new use and date value is mandatory')
                );
              }
              return Promise.resolve();
            },
          },
        ]}
        noStyle
      />

      <Spin spinning={isFetching}>
        <Form.Item noStyle shouldUpdate={shouldUpdateCompare(fieldName)}>
          {({ getFieldValue }) => (
            <DataGrid
              columns={columns}
              dataSource={getFieldValue(fieldName)}
              pagination={false}
              rowKey="id"
              size="small"
              hideFilters
            />
          )}
        </Form.Item>

        <Row>
          <FormInput col xs={3} name="flagupdatefv" valuePropName="checked">
            <Switch />
          </FormInput>
        </Row>
        <Row gutter={16}>
          <FormInput col xs={3} name="flagupdatemv" valuePropName="checked">
            <Switch />
          </FormInput>
          <Form.Item shouldUpdate={shouldUpdateCompare('flagupdatemv')} noStyle>
            {({ getFieldValue }) =>
              getFieldValue('flagupdatemv') && (
                <>
                  <FormInput
                    col
                    xs={6}
                    rules={[{ required: true }]}
                    name="newmarketvalue"
                  >
                    <InputCurrency currcode={localStorage.getItem('currcode') ?? ''} />
                  </FormInput>
                  <FormInput
                    col
                    xs={6}
                    rules={[{ required: true }]}
                    name="newmarketvaluedate"
                  >
                    <DatePicker format="YYYY-MM-DD" />
                  </FormInput>
                  <FormInput
                    col
                    xs={6}
                    rules={[{ required: true }]}
                    name="mvsource"
                  >
                    <ParListSelect parcode="MVSOURCE" />
                  </FormInput>
                </>
              )
            }
          </Form.Item>
        </Row>

        <Row gutter={[8, 8]}>
          <FormInput col xs={18} name="comment" rules={[{ required: true }]}>
            <TextArea rows={4} />
          </FormInput>
        </Row>
      </Spin>
    </DF.Provider>
  );
};
export default TabAddAssetUse;
